import * as React from "react";
import { Link, useNavigate } from "react-router";
import { DropdownMenu } from "radix-ui";
import { NotificationBell } from "@ozpanel/kit";
import { ArrowLeft, ChevronDown, LogOut, Monitor, Moon, Sun, ShieldCheck, TriangleAlert, UserCircle2, Menu } from "lucide-react";
import { Brand, Button, Eyebrow, Pill, cn, formatRelative, useAuth, useTheme, type ThemeChoice } from "@ozpanel/ui";

// Topbar — the account menu, notification centre and theme control the console had none of.
// Sticky and translucent with a blur, per v1.

export interface Notice {
  id: string;
  title: string;
  detail?: string;
  tone: "ok" | "warn" | "crit";
  at?: string;
  to?: string;
}

export function Topbar({ notices, onMenu }: { notices: Notice[]; onMenu?: () => void }) {
  const { who, logout, impersonating, stopImpersonating } = useAuth();
  const navigate = useNavigate();
  const unread = notices.filter((n) => n.tone !== "ok").length;

  return (
    <header className="sticky top-0 z-20 flex h-[58px] flex-none items-center gap-4 border-b border-line px-4 topbar-blur">
      {/* The drawer handle, only where there is a drawer. Above lg the sidebar is always in the
          flow and a menu button would open nothing. */}
      {onMenu && (
        <button
          type="button"
          aria-label="Menu"
          onClick={onMenu}
          className="-ml-1 grid size-9 shrink-0 place-items-center rounded-lg text-muted hover:bg-surface2 hover:text-fg lg:hidden"
        >
          <Menu size={18} />
        </button>
      )}
      <Link to="/" className="flex items-center">
        <Brand />
      </Link>

      {impersonating && (
        <div className="flex items-center gap-2 rounded-lg border border-[var(--ozp-accent-line)] bg-accentsoft px-2.5 py-1.5">
          <span className="font-mono text-[11px] text-accent">acting as {who?.impersonating ?? who?.email}</span>
          <button
            type="button"
            onClick={() => void stopImpersonating()}
            className="flex items-center gap-1 text-[11.5px] font-semibold text-accent hover:underline"
          >
            <ArrowLeft size={11} />
            back
          </button>
        </div>
      )}

      <div className="ml-auto flex items-center gap-1.5">
        {/* ONE bell, and beside it a warning triangle — because they answer different questions
            and used to be indistinguishable.
   
            Both were rendered as the same bell icon, the same size, adjacent, each with its own
            red count, and both labelled "Notifications". Worse, they overlapped: main.go installs
            `srv.Realtime().WithSink(… notificationsMod.Record …)`, so every warn/crit bus event is
            written to the durable feed AND was listed here live — the same event, twice, in two
            controls a reader could not tell apart.

            The split that survives is the one that is real:
              BELL      what HAPPENED — the durable feed, including while nobody was watching.
              TRIANGLE  what is WRONG NOW — a module that is degraded, an account with no backup.
                        These are states, not occurrences: nothing re-emits them, so a feed of
                        events cannot show them and they would be lost by merging.
            Different icon, different label, no shared content. */}
        <NotificationBell />
        <AttentionMenu notices={notices} unread={unread} onGo={(to) => navigate(to)} />
        <ThemeMenu />
        <AccountMenu
          email={who?.email}
          role={who?.role}
          impersonating={impersonating}
          onGoSecurity={() => navigate("/security")}
          onStopImpersonating={() => void stopImpersonating()}
          onSignOut={() => void logout()}
        />
      </div>
    </header>
  );
}

const menuContent =
  "z-50 min-w-[240px] rounded-[11px] border border-line bg-surface p-1.5 shadow-[0_18px_50px_-12px_rgba(0,0,0,.5)]";
const menuItem =
  "flex cursor-pointer items-center gap-2.5 rounded-[7px] px-2.5 py-2 text-[13px] text-fg outline-none data-[highlighted]:bg-surface2";

function IconButton({
  label,
  children,
  badge,
}: {
  label: string;
  children: React.ReactNode;
  badge?: number;
}) {
  return (
    <span
      aria-label={label}
      className="relative grid size-[34px] place-items-center rounded-lg border border-transparent text-muted transition-colors hover:border-line hover:bg-surface hover:text-fg"
    >
      {children}
      {badge !== undefined && badge > 0 && (
        <span className="absolute -top-0.5 -right-0.5 grid min-w-[15px] place-items-center rounded-full bg-crit px-1 font-mono text-[9px] leading-[15px] text-white">
          {badge > 9 ? "9+" : badge}
        </span>
      )}
    </span>
  );
}

function AttentionMenu({ notices, unread, onGo }: { notices: Notice[]; unread: number; onGo(to: string): void }) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button type="button">
          <IconButton label={unread > 0 ? `${unread} things need attention` : "Needs attention"} badge={unread}>
            <TriangleAlert size={16} />
          </IconButton>
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="end" sideOffset={6} className={cn(menuContent, "w-[340px] p-0")}>
          <div className="flex items-center justify-between border-b border-line px-3 py-2.5">
            <Eyebrow accent>Needs attention</Eyebrow>
            <div className="flex items-center gap-2">
              {/* No live/reconnecting pill here any more. This list is built from POLLED queries
                  (module health, backup coverage), so reporting the stream's connection state
                  beside it qualified a value with something that does not govern it. It moved to
                  the notification bell, which is the surface the stream actually feeds. */}
              {unread > 0 && <Pill tone="warn">{unread}</Pill>}
              {/* What is breached NOW on every node, from each node's alert engine — this menu is
                  module health and backups. */}
              <Link to="/alerts" className="text-[11.5px] text-accent hover:underline">
                Open alerts
              </Link>
              <Link to="/notifications" className="text-[11.5px] text-accent hover:underline">
                Settings
              </Link>
            </div>
          </div>
          <div className="max-h-[320px] overflow-y-auto p-1.5">
            {notices.length === 0 ? (
              <p className="px-2.5 py-6 text-center text-[13px] text-muted">
                Nothing needs attention.
              </p>
            ) : (
              notices.map((n) => (
                <DropdownMenu.Item
                  key={n.id}
                  onSelect={() => n.to && onGo(n.to)}
                  className={cn(menuItem, "items-start")}
                >
                  <span
                    className={cn(
                      "mt-1.5 size-1.5 shrink-0 rounded-full",
                      n.tone === "crit" ? "bg-crit" : n.tone === "warn" ? "bg-warn" : "bg-ok",
                    )}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{n.title}</span>
                    {n.detail && <span className="mt-0.5 block text-[12px] text-muted">{n.detail}</span>}
                  </span>
                  {n.at && <span className="shrink-0 font-mono text-[10px] text-faint">{formatRelative(n.at)}</span>}
                </DropdownMenu.Item>
              ))
            )}
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

const THEMES: { key: ThemeChoice; label: string; icon: React.ReactNode }[] = [
  { key: "dark", label: "Dark", icon: <Moon size={14} /> },
  { key: "light", label: "Light", icon: <Sun size={14} /> },
  { key: "system", label: "Match system", icon: <Monitor size={14} /> },
];

function ThemeMenu() {
  const { choice, resolved, setChoice } = useTheme();
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button type="button">
          <IconButton label="Theme">{resolved === "dark" ? <Moon size={16} /> : <Sun size={16} />}</IconButton>
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="end" sideOffset={6} className={menuContent}>
          {THEMES.map((t) => (
            <DropdownMenu.Item key={t.key} onSelect={() => setChoice(t.key)} className={menuItem}>
              <span className="text-muted">{t.icon}</span>
              {t.label}
              {choice === t.key && <span className="ml-auto text-accent">✓</span>}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function AccountMenu({
  email,
  onGoSecurity,
  role,
  impersonating,
  onStopImpersonating,
  onSignOut,
}: {
  email?: string;
  onGoSecurity?(): void;
  role?: string;
  impersonating?: boolean;
  onStopImpersonating?(): void;
  onSignOut(): void;
}) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className="ml-1 flex items-center gap-2 rounded-lg border border-line bg-surface py-1 pr-2 pl-2.5 text-left transition-colors hover:border-[var(--ozp-accent-line)]"
        >
          <span className="grid size-[22px] place-items-center rounded-full bg-accentsoft text-accent">
            <UserCircle2 size={14} />
          </span>
          <span className="hidden min-w-0 sm:block">
            <span className="block max-w-[150px] truncate text-[12.5px] leading-tight font-semibold">{email}</span>
            <span className="block font-mono text-[10px] text-faint">{role}</span>
          </span>
          <ChevronDown size={13} className="text-faint" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="end" sideOffset={6} className={menuContent}>
          <div className="border-b border-line px-2.5 pt-1.5 pb-2.5">
            <p className="truncate text-[13px] font-semibold">{email}</p>
            <p className="mt-0.5 font-mono text-[10.5px] text-faint">{role}</p>
          </div>
          <div className="pt-1.5">
            {/* Your own security lives on the account menu, which is where a person looks for it
                — the admin console had no route to it at all, so a platform administrator could
                not see their own sessions or turn on a second factor while both customer consoles
                could. */}
            <DropdownMenu.Item onSelect={() => onGoSecurity?.()} className={menuItem}>
              <ShieldCheck size={14} />
              Your security
            </DropdownMenu.Item>
            {impersonating && onStopImpersonating && (
              <DropdownMenu.Item onSelect={onStopImpersonating} className={cn(menuItem, "text-accent")}>
                <ArrowLeft size={14} />
                Return to my account
              </DropdownMenu.Item>
            )}
            <DropdownMenu.Item onSelect={onSignOut} className={cn(menuItem, "text-crit")}>
              <LogOut size={14} />
              Sign out
            </DropdownMenu.Item>
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

export { Button };
